import * as THREE from 'three';

// Time keyframes: hour → scene state
// Each entry: { h, sky, fog, fogDensity, ambI, ambColor, sunI, sunColor, hemiI }
const KEYS = [
  { h:  0, sky: 0x060a1a, fog: 0x05080f, fogD: 0.0050, ambI: 0.06, ambC: 0x1a2040, sunI: 0.0,  sunC: 0xfff3d0, hemiI: 0.04 },
  { h:  5, sky: 0x1a1a3a, fog: 0x12121e, fogD: 0.0045, ambI: 0.09, ambC: 0x1a2040, sunI: 0.0,  sunC: 0xfff3d0, hemiI: 0.06 },
  { h:  6, sky: 0xe05018, fog: 0xcc4010, fogD: 0.0038, ambI: 0.22, ambC: 0xffcc88, sunI: 0.25, sunC: 0xff8833, hemiI: 0.12 },
  { h:  8, sky: 0x5a9fd8, fog: 0x70aed0, fogD: 0.0030, ambI: 0.50, ambC: 0xfff0d8, sunI: 0.80, sunC: 0xffe8b0, hemiI: 0.28 },
  { h: 12, sky: 0x87ceeb, fog: 0x9bc8d8, fogD: 0.0028, ambI: 0.65, ambC: 0xfff8e7, sunI: 1.15, sunC: 0xfff3d0, hemiI: 0.35 },
  { h: 16, sky: 0x78b8e0, fog: 0x88b4cc, fogD: 0.0030, ambI: 0.58, ambC: 0xfff0d0, sunI: 0.90, sunC: 0xffe0a0, hemiI: 0.30 },
  { h: 18, sky: 0xe06020, fog: 0xcc5018, fogD: 0.0038, ambI: 0.28, ambC: 0xffcc88, sunI: 0.40, sunC: 0xff7733, hemiI: 0.14 },
  { h: 20, sky: 0x1e1a38, fog: 0x161220, fogD: 0.0044, ambI: 0.10, ambC: 0x1a2040, sunI: 0.0,  sunC: 0xfff3d0, hemiI: 0.07 },
  { h: 24, sky: 0x060a1a, fog: 0x05080f, fogD: 0.0050, ambI: 0.06, ambC: 0x1a2040, sunI: 0.0,  sunC: 0xfff3d0, hemiI: 0.04 },
];

function lerp(a, b, t) { return a + (b - a) * t; }
function lerpColor(ca, cb, t) { return new THREE.Color(ca).lerp(new THREE.Color(cb), t); }
function easeInOut(t) { return t * t * (3 - 2 * t); }

export class DayNightCycle {
  /**
   * @param {THREE.Scene}            scene
   * @param {THREE.DirectionalLight} sun
   * @param {THREE.AmbientLight}     ambient
   * @param {THREE.HemisphereLight}  hemi
   */
  constructor(scene, sun, ambient, hemi) {
    this._scene   = scene;
    this._sun     = sun;
    this._ambient = ambient;
    this._hemi    = hemi;
    this._hour    = 12; // noon by default
    this._useReal = false;
    this._apply();
  }

  /** Set time of day (0–24 hours). */
  setHour(h) {
    this._hour    = ((h % 24) + 24) % 24;
    this._useReal = false;
    this._apply();
  }

  /** Switch to real-clock mode (reads browser time). */
  setRealTime(enabled) {
    this._useReal = enabled;
    if (enabled) this._syncReal();
  }

  get hour()    { return this._hour; }
  get isNight() { return this._hour < 6 || this._hour >= 20; }

  update() {
    if (this._useReal) this._syncReal();
  }

  _syncReal() {
    const now = new Date();
    this._hour = now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;
    this._apply();
  }

  _apply() {
    const h = this._hour;

    // Find surrounding keyframes
    let i0 = KEYS.length - 2;
    for (let i = 0; i < KEYS.length - 1; i++) {
      if (h <= KEYS[i + 1].h) { i0 = i; break; }
    }
    const k0 = KEYS[i0];
    const k1 = KEYS[i0 + 1];
    const t  = easeInOut((h - k0.h) / (k1.h - k0.h));

    // Sky background
    this._scene.background = lerpColor(k0.sky, k1.sky, t);

    // Fog
    if (this._scene.fog) {
      this._scene.fog.color.copy(lerpColor(k0.fog, k1.fog, t));
      this._scene.fog.density = lerp(k0.fogD, k1.fogD, t);
    }

    // Ambient light
    this._ambient.intensity = lerp(k0.ambI, k1.ambI, t);
    this._ambient.color.copy(lerpColor(k0.ambC, k1.ambC, t));

    // Sun directional light
    const sunI = lerp(k0.sunI, k1.sunI, t);
    this._sun.intensity = sunI;
    this._sun.visible   = sunI > 0.005;
    this._sun.color.copy(lerpColor(k0.sunC, k1.sunC, t));

    // Sun position: simple elliptical orbit
    const angle = (h / 24) * Math.PI * 2 - Math.PI * 0.5;
    const elev  = Math.sin((h - 6) / 12 * Math.PI); // 0 at dawn/dusk, 1 at noon
    this._sun.position.set(
      Math.cos(angle) * 400,
      Math.max(10, elev * 450),
      Math.sin(angle) * 250,
    );

    // Hemisphere fill
    this._hemi.intensity = lerp(k0.hemiI, k1.hemiI, t);
  }
}
